import { ShieldCheck, History, FileSearch, Users } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import KontrolleVisual from "./KontrolleVisual";
import { useLanguage } from "@/hooks/useLanguage";

const icons = [ShieldCheck, History, FileSearch, Users] as const;
const colors = ["mint", "orange", "mint", "orange"] as const;

const colorMap = {
  mint: "bg-chathero-mint/15 text-chathero-mint",
  orange: "bg-chathero-orange/15 text-chathero-orange",
};

const SteuerungQualitaetSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject } = useLanguage();
  const features = tObject<Array<{ title: string; description: string }>>("knowledgeBasePage.steuerung.features");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-center mb-8">
                <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
                  {t("knowledgeBasePage.steuerung.badge")}
                </Badge>

                <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                  <span className="text-foreground">{t("knowledgeBasePage.steuerung.title")}</span>
                </h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">{t("knowledgeBasePage.steuerung.intro")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {features.map((feature, index) => {
                  const Icon = icons[index];
                  const color = colors[index];
                  return (
                    <div
                      key={feature.title}
                      className={cn(
                        "bg-background rounded-2xl p-5 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all",
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                      )}
                      style={{
                        transitionDelay: isVisible ? `${200 + index * 100}ms` : "0ms",
                        transitionDuration: "600ms",
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", colorMap[color])}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground mb-1">{feature.title}</h3>
                          <p className="text-muted-foreground text-xs leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className={cn(
                  "bg-chathero-mint/10 border border-chathero-mint/20 rounded-2xl p-5 transition-all duration-500",
                  isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                )}
                style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
              >
                <p className="text-sm text-primary font-medium">{t("knowledgeBasePage.steuerung.highlight")}</p>
              </div>
            </div>

            <div className="order-2 lg:order-none">
              <KontrolleVisual />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SteuerungQualitaetSection;
